import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import { RCP_OPTIONS, RCP_DEFAUT, CULTURES_SUBSTITUTION } from '../../shared/constants';
import { useHorizon } from '../../shared/hooks/useHorizon';
import { libelleHorizon } from '../../shared/utils/horizon';
import NuageSubstitutionChart from '../../shared/charts/NuageSubstitutionChart';

/** Adaptations / Substitution de culture (mock). */
export default function Substitution() {
  const { idExploit } = useOAD();
  const [cultureARemplacer, setCultureARemplacer] = React.useState(null);
  const [rcp, setRcp] = React.useState(RCP_DEFAUT);
  // En TRACC, l'année (2035/2050) n'a pas de sens : on bascule sur les
  // niveaux de réchauffement (+2°C / +2.7°C), cf. shared/utils/horizon.js.
  const [annee, setAnnee, anneesPossibles] = useHorizon(rcp, {
    // 2020 (ou la référence en TRACC) proposée en plus de 2035/2050 ; 2035 (ou
    // +2°C) reste coché à l'ouverture.
    avecReference: true,
    indexParDefaut: 1,
  });

  const parametresExploitation = React.useMemo(
    () => ({ idExploitation: idExploit }),
    [idExploit]
  );
  const { data: assolement } = useApiData(api.getProductionRapport, parametresExploitation);

  // Ici toutes les cultures de l'assolement sont proposées, sans filtre :
  // n'importe laquelle peut être remplacée.
  const cultures = React.useMemo(
    () => (assolement ? Object.keys(assolement) : []),
    [assolement]
  );

  React.useEffect(() => {
    if (cultures.length > 0 && !cultures.includes(cultureARemplacer)) {
      setCultureARemplacer(cultures[0]);
    }
  }, [cultures, cultureARemplacer]);

  const parametres = React.useMemo(
    () => ({
      idExploitation: idExploit,
      annee,
      cultureARemplacer,
      rcp,
      // Les remplaçantes candidates sont une liste fixe, indépendante de
      // l'assolement de l'utilisateur.
      cultures: CULTURES_SUBSTITUTION,
    }),
    [idExploit, annee, cultureARemplacer, rcp]
  );
  const { data, loading, error } = useApiData(api.getSubstitution, parametres, {
    skip: !cultureARemplacer,
  });

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="2035-2050"
        niveau2="adaptations"
        niveau3="substitution"
        idExploit={idExploit}
      >
        <label className="sidebar-champ">
          <span className="sidebar-label">{rcp === 'tracc' ? 'Réchauffement' : 'Année'}</span>
          <select value={annee} onChange={(e) => setAnnee(e.target.value)}>
            {anneesPossibles.map((h) => (
              <option key={h} value={h}>{libelleHorizon(rcp, h)}</option>
            ))}
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Culture à remplacer</span>
          <select
            value={cultureARemplacer ?? ''}
            onChange={(e) => setCultureARemplacer(e.target.value)}
            disabled={cultures.length === 0}
          >
            {cultures.length === 0 && <option>Assolement en cours de chargement</option>}
            {cultures.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-substitution"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">
            Remplacer {cultureARemplacer ?? 'une culture'} — effet sur le produit brut et la perte
          </h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading ? (
            <p className="tdb-chargement">Chargement…</p>
          ) : (
            !error && <LimiteErreur cle={`${annee}-${cultureARemplacer}-${rcp}`}>
                <NuageSubstitutionChart data={data?.cultures ?? []} />
              </LimiteErreur>
          )}
        </section>
      </main>
    </div>
  );
}
